import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, TextField, Divider, IconButton, Button } from '@mui/material';
import { Add, Delete, CloudUpload } from '@mui/icons-material';
import { Color } from '../../../assets/color';
import { api_urls } from '../../../utils/api-urls';
import RouteHeader from '../../../components/common/RouteHeader';
import DatatableHeading from '../../../components/heading/DatatableHeading';
import * as SuperadminActions from '../../../redux/actions/superadminAction';

const SiteSettings = () => {
    const dispatch = useDispatch();
    const { siteSettingsData } = useSelector(state => state?.superadminReducer);
    const fileInputRef = useRef(null);
    const [innovatorImageFile, setInnovatorImageFile] = useState(null);
    const [innovatorImagePreview, setInnovatorImagePreview] = useState('');
    const [bizVideoImageFiles, setBizVideoImageFiles] = useState({});
    const bizVideoImageRefs = [useRef(null), useRef(null), useRef(null), useRef(null), useRef(null)];
    const [websiteImageFiles, setWebsiteImageFiles] = useState({});
    const websiteImageKeys = ['hero_slide_1','hero_slide_2','hero_slide_3','hero_slide_4','about_section','about_ai_section','benefits_section','why_powerful_section','intl_affiliation_section','remedies_1','remedies_2','remedies_3','remedies_4','usa_affiliation_1','usa_affiliation_2','usa_affiliation_3','usa_affiliation_4','biz_opportunity_1','biz_opportunity_2','biz_opportunity_3','biz_opportunity_4','partner_benefit_section','partner_step_1','partner_step_2','partner_step_3'];
    const websiteImageRefs = { hero_slide_1: useRef(null), hero_slide_2: useRef(null), hero_slide_3: useRef(null), hero_slide_4: useRef(null), about_section: useRef(null), about_ai_section: useRef(null), benefits_section: useRef(null), why_powerful_section: useRef(null), intl_affiliation_section: useRef(null), remedies_1: useRef(null), remedies_2: useRef(null), remedies_3: useRef(null), remedies_4: useRef(null), usa_affiliation_1: useRef(null), usa_affiliation_2: useRef(null), usa_affiliation_3: useRef(null), usa_affiliation_4: useRef(null), biz_opportunity_1: useRef(null), biz_opportunity_2: useRef(null), biz_opportunity_3: useRef(null), biz_opportunity_4: useRef(null), partner_benefit_section: useRef(null), partner_step_1: useRef(null), partner_step_2: useRef(null), partner_step_3: useRef(null) };

    const [formData, setFormData] = useState({
        youtube_video_link: '',
        demo_video_link: '',
        footer_copyright: '',
        social_media_links: {
            facebook: '',
            instagram: '',
            youtube: '',
            linkedin: '',
            twitter: '',
        },
        innovator_section: {
            name: '',
            title: '',
            description: '',
            image: '',
        },
        business_opportunity_videos: [],
    });

    useEffect(() => {
        dispatch(SuperadminActions.getSiteSettings());
    }, []);

    useEffect(() => {
        if (siteSettingsData) {
            setFormData({
                youtube_video_link: siteSettingsData?.youtube_video_link || '',
                demo_video_link: siteSettingsData?.demo_video_link || '',
                footer_copyright: siteSettingsData?.footer_copyright || '',
                social_media_links: {
                    facebook: siteSettingsData?.social_media_links?.facebook || '',
                    instagram: siteSettingsData?.social_media_links?.instagram || '',
                    youtube: siteSettingsData?.social_media_links?.youtube || '',
                    linkedin: siteSettingsData?.social_media_links?.linkedin || '',
                    twitter: siteSettingsData?.social_media_links?.twitter || '',
                },
                innovator_section: {
                    name: siteSettingsData?.innovator_section?.name || '',
                    title: siteSettingsData?.innovator_section?.title || '',
                    description: siteSettingsData?.innovator_section?.description || '',
                    image: siteSettingsData?.innovator_section?.image || '',
                },
                business_opportunity_videos: siteSettingsData?.business_opportunity_videos || [],
            });
            // Set existing image preview
            if (siteSettingsData?.innovator_section?.image) {
                setInnovatorImagePreview(siteSettingsData.innovator_section.image.startsWith('http') ? siteSettingsData.innovator_section.image : api_urls + siteSettingsData.innovator_section.image);
            }
        }
    }, [siteSettingsData]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSocialChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            social_media_links: { ...prev.social_media_links, [field]: value }
        }));
    };

    const handleInnovatorChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            innovator_section: { ...prev.innovator_section, [field]: value }
        }));
    };

    const handleBusinessVideoChange = (index, field, value) => {
        setFormData(prev => {
            const updatedVideos = [...prev.business_opportunity_videos];
            updatedVideos[index] = { ...updatedVideos[index], [field]: value };
            return { ...prev, business_opportunity_videos: updatedVideos };
        });
    };

    const addBusinessVideo = () => {
        setFormData(prev => ({
            ...prev,
            business_opportunity_videos: [...prev.business_opportunity_videos, { title: '', video_link: '', thumbnail: '' }]
        }));
    };

    const removeBusinessVideo = (index) => {
        setFormData(prev => ({
            ...prev,
            business_opportunity_videos: prev.business_opportunity_videos.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = () => {
        const fd = new FormData();
        fd.append('youtube_video_link', formData.youtube_video_link);
        fd.append('demo_video_link', formData.demo_video_link);
        fd.append('footer_copyright', formData.footer_copyright);
        fd.append('social_media_links', JSON.stringify(formData.social_media_links));
        fd.append('innovator_section', JSON.stringify(formData.innovator_section));
        fd.append('business_opportunity_videos', JSON.stringify(formData.business_opportunity_videos));
        if (innovatorImageFile) {
            fd.append('innovator_image', innovatorImageFile);
        }
        Object.entries(bizVideoImageFiles).forEach(([idx, file]) => {
            if (file) fd.append(`business_opportunity_image_${idx}`, file);
        });
        Object.entries(websiteImageFiles).forEach(([key, file]) => {
            if (file) fd.append(`website_image_${key}`, file);
        });
        const payload = {
            data: fd,
            onComplete: () => { }
        };
        dispatch(SuperadminActions.createUpdateSiteSettings(payload));
    };

    return (
        <>
            <RouteHeader title="Site Settings" sub_title="Manage global site settings for your platform" />

            <div style={{ backgroundColor: Color.white, padding: '20px', borderRadius: '10px' }}>
                <DatatableHeading title="General Settings" sub_title="Configure main website links and content" />

                <Grid container spacing={3} sx={{ marginTop: '10px' }}>
                    {/* General */}
                    <Grid item lg={6} sm={12} md={6} xs={12}>
                        <TextField label="YouTube Video Link (Hero Section)" variant="outlined" fullWidth
                            value={formData.youtube_video_link}
                            onChange={(e) => handleChange('youtube_video_link', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid item lg={6} sm={12} md={6} xs={12}>
                        <TextField label="Demo Video Link" variant="outlined" fullWidth
                            value={formData.demo_video_link}
                            onChange={(e) => handleChange('demo_video_link', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid item lg={12} sm={12} md={12} xs={12}>
                        <TextField label="Footer Copyright Text" variant="outlined" fullWidth
                            value={formData.footer_copyright}
                            onChange={(e) => handleChange('footer_copyright', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Social Media Links */}
                <DatatableHeading title="Social Media Links" sub_title="Configure social media URLs for the website" />

                <Grid container spacing={3} sx={{ marginTop: '10px' }}>
                    <Grid item lg={4} sm={12} md={6} xs={12}>
                        <TextField label="Facebook URL" variant="outlined" fullWidth
                            value={formData.social_media_links.facebook}
                            onChange={(e) => handleSocialChange('facebook', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item lg={4} sm={12} md={6} xs={12}>
                        <TextField label="Instagram URL" variant="outlined" fullWidth
                            value={formData.social_media_links.instagram}
                            onChange={(e) => handleSocialChange('instagram', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item lg={4} sm={12} md={6} xs={12}>
                        <TextField label="YouTube URL" variant="outlined" fullWidth
                            value={formData.social_media_links.youtube}
                            onChange={(e) => handleSocialChange('youtube', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item lg={4} sm={12} md={6} xs={12}>
                        <TextField label="LinkedIn URL" variant="outlined" fullWidth
                            value={formData.social_media_links.linkedin}
                            onChange={(e) => handleSocialChange('linkedin', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item lg={4} sm={12} md={6} xs={12}>
                        <TextField label="Twitter URL" variant="outlined" fullWidth
                            value={formData.social_media_links.twitter}
                            onChange={(e) => handleSocialChange('twitter', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Innovator Section */}
                <DatatableHeading title="Innovator Section" sub_title="Configure the innovator/founder information on the website" />

                <Grid container spacing={3} sx={{ marginTop: '10px' }}>
                    <Grid item lg={6} sm={12} md={6} xs={12}>
                        <TextField label="Innovator Name" variant="outlined" fullWidth
                            value={formData.innovator_section.name}
                            onChange={(e) => handleInnovatorChange('name', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item lg={6} sm={12} md={6} xs={12}>
                        <TextField label="Innovator Title" variant="outlined" fullWidth
                            value={formData.innovator_section.title}
                            onChange={(e) => handleInnovatorChange('title', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item lg={12} sm={12} md={12} xs={12}>
                        <TextField label="Innovator Description" variant="outlined" fullWidth multiline rows={3}
                            value={formData.innovator_section.description}
                            onChange={(e) => handleInnovatorChange('description', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item lg={12} sm={12} md={12} xs={12}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                            <input type="file" accept="image/*" ref={fileInputRef} style={{ display: 'none' }}
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        setInnovatorImageFile(file);
                                        setInnovatorImagePreview(URL.createObjectURL(file));
                                    }
                                }}
                            />
                            <Button variant="outlined" startIcon={<CloudUpload />}
                                onClick={() => fileInputRef.current?.click()}
                                sx={{ borderColor: Color.primary, color: Color.primary, textTransform: 'none', padding: '10px 20px', '&:hover': { borderColor: '#5443c8', backgroundColor: '#f5f3ff' } }}
                            >
                                {innovatorImageFile ? 'Change Image' : 'Browse Innovator Image'}
                            </Button>
                            {innovatorImageFile && <span style={{ fontSize: '13px', color: '#666' }}>{innovatorImageFile.name}</span>}
                            {innovatorImagePreview && (
                                <div style={{ marginTop: '8px', width: '100%' }}>
                                    <img src={innovatorImagePreview} alt="Innovator Preview"
                                        style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '8px', border: '1px solid #ddd', objectFit: 'cover' }}
                                    />
                                </div>
                            )}
                        </div>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Business Opportunity Videos */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <DatatableHeading title="Business Opportunity Videos" sub_title="Manage video links for business opportunity section" />
                    <IconButton onClick={addBusinessVideo} sx={{ backgroundColor: Color.primary, color: '#fff', '&:hover': { backgroundColor: '#5443c8' } }}>
                        <Add />
                    </IconButton>
                </div>

                {formData.business_opportunity_videos.map((video, index) => (
                    <Grid container spacing={3} sx={{ marginTop: '5px', padding: '10px', border: '1px solid #eee', borderRadius: '8px' }} key={index}>
                        <Grid item lg={4} sm={12} md={4} xs={12}>
                            <TextField label={`Video ${index + 1} - Title`} variant="outlined" fullWidth
                                value={video.title}
                                onChange={(e) => handleBusinessVideoChange(index, 'title', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item lg={4} sm={12} md={4} xs={12}>
                            <TextField label={`Video ${index + 1} - YouTube Link`} variant="outlined" fullWidth
                                value={video.video_link}
                                onChange={(e) => handleBusinessVideoChange(index, 'video_link', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item lg={3} sm={10} md={3} xs={10}>
                            <div>
                                <input
                                    type="file"
                                    accept="image/*"
                                    ref={bizVideoImageRefs[index]}
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) setBizVideoImageFiles(prev => ({ ...prev, [index]: file }));
                                    }}
                                />
                                <Button variant="outlined" startIcon={<CloudUpload />} fullWidth
                                    onClick={() => bizVideoImageRefs[index]?.current?.click()}
                                    sx={{ height: '56px', textTransform: 'none', borderColor: '#ccc', color: '#555' }}
                                >
                                    {bizVideoImageFiles[index] ? bizVideoImageFiles[index].name : `Card ${index + 1} Image`}
                                </Button>
                                {(bizVideoImageFiles[index] || video.thumbnail) && (
                                    <img
                                        src={bizVideoImageFiles[index] ? URL.createObjectURL(bizVideoImageFiles[index]) : (video.thumbnail?.startsWith('http') ? video.thumbnail : api_urls + video.thumbnail)}
                                        alt="preview"
                                        style={{ marginTop: '8px', width: '100%', height: '80px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #ddd' }}
                                    />
                                )}
                            </div>
                        </Grid>
                        <Grid item lg={1} sm={2} md={1} xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton onClick={() => removeBusinessVideo(index)} sx={{ color: Color.red }}>
                                <Delete />
                            </IconButton>
                        </Grid>
                    </Grid>
                ))}


                {/* Website Section Images */}
                <Divider sx={{ my: 3 }} />
                <DatatableHeading title="Website Section Images" sub_title="Upload images for each section of the main website & landing page. These replace the default images live." />
                <Grid container spacing={2} sx={{ marginTop: '10px' }}>

                        <Grid item lg={3} sm={12} md={6} xs={12}>
                            <div style={{ border: '1px solid #e0e0e0', borderRadius: '10px', padding: '14px', background: '#fafafa' }}>
                                <p style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px', color: '#333' }}>
                                    Hero Slider – Slide 1
                                </p>
                                <p style={{ fontSize: '11px', color: '#888', marginBottom: '8px' }}>Size: 520×520 px &nbsp;|&nbsp; Main hero carousel, Slide 1 (robot/AI image)</p>
                                <input
                                    type="file"
                                    accept="image/*"
                                    ref={websiteImageRefs['hero_slide_1']}
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) setWebsiteImageFiles(prev => ({ ...prev, 'hero_slide_1': file }));
                                    }}
                                />
                                <Button variant="outlined" startIcon={<CloudUpload />} fullWidth
                                    onClick={() => websiteImageRefs['hero_slide_1']?.current?.click()}
                                    sx={{ height: '44px', textTransform: 'none', borderColor: '#ccc', color: '#555', fontSize: '12px' }}
                                >
                                    {websiteImageFiles['hero_slide_1'] ? websiteImageFiles['hero_slide_1'].name : 'Upload Image'}
                                </Button>
                                {(websiteImageFiles['hero_slide_1'] || siteSettingsData?.website_images?.['hero_slide_1']) && (
                                    <img
                                        src={websiteImageFiles['hero_slide_1'] ? URL.createObjectURL(websiteImageFiles['hero_slide_1']) : (siteSettingsData.website_images['hero_slide_1']?.startsWith('http') ? siteSettingsData.website_images['hero_slide_1'] : api_urls + siteSettingsData.website_images['hero_slide_1'])}
                                        alt="Hero Slider – Slide 1"
                                        style={{ marginTop: '8px', width: '100%', height: '90px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #ddd', background: '#f5f5f5' }}
                                    />
                                )}
                            </div>
                        </Grid>
                        <Grid item lg={3} sm={12} md={6} xs={12}>
                            <div style={{ border: '1px solid #e0e0e0', borderRadius: '10px', padding: '14px', background: '#fafafa' }}>
                                <p style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px', color: '#333' }}>
                                    Hero Slider – Slide 2
                                </p>
                                <p style={{ fontSize: '11px', color: '#888', marginBottom: '8px' }}>Size: 520×520 px &nbsp;|&nbsp; Main hero carousel, Slide 2</p>
                                <input
                                    type="file"
                                    accept="image/*"
                                    ref={websiteImageRefs['hero_slide_2']}
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) setWebsiteImageFiles(prev => ({ ...prev, 'hero_slide_2': file }));
                                    }}
                                />
                                <Button variant="outlined" startIcon={<CloudUpload />} fullWidth
                                    onClick={() => websiteImageRefs['hero_slide_2']?.current?.click()}
                                    sx={{ height: '44px', textTransform: 'none', borderColor: '#ccc', color: '#555', fontSize: '12px' }}
                                >
                                    {websiteImageFiles['hero_slide_2'] ? websiteImageFiles['hero_slide_2'].name : 'Upload Image'}
                                </Button>
                                {(websiteImageFiles['hero_slide_2'] || siteSettingsData?.website_images?.['hero_slide_2']) && (
                                    <img
                                        src={websiteImageFiles['hero_slide_2'] ? URL.createObjectURL(websiteImageFiles['hero_slide_2']) : (siteSettingsData.website_images['hero_slide_2']?.startsWith('http') ? siteSettingsData.website_images['hero_slide_2'] : api_urls + siteSettingsData.website_images['hero_slide_2'])}
                                        alt="Hero Slider – Slide 2"
                                        style={{ marginTop: '8px', width: '100%', height: '90px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #ddd', background: '#f5f5f5' }}
                                    />
                                )}
                            </div>
                        </Grid>
                        <Grid item lg={3} sm={12} md={6} xs={12}>
                            <div style={{ border: '1px solid #e0e0e0', borderRadius: '10px', padding: '14px', background: '#fafafa' }}>
                                <p style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px', color: '#333' }}>
                                    Hero Slider – Slide 3
                                </p>
                                <p style={{ fontSize: '11px', color: '#888', marginBottom: '8px' }}>Size: 520×520 px &nbsp;|&nbsp; Main hero carousel, Slide 3</p>
                                <input
                                    type="file"
                                    accept="image/*"
                                    ref={websiteImageRefs['hero_slide_3']}
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) setWebsiteImageFiles(prev => ({ ...prev, 'hero_slide_3': file }));
                                    }}
                                />
                                <Button variant="outlined" startIcon={<CloudUpload />} fullWidth
                                    onClick={() => websiteImageRefs['hero_slide_3']?.current?.click()}
                                    sx={{ height: '44px', textTransform: 'none', borderColor: '#ccc', color: '#555', fontSize: '12px' }}
                                >
                                    {websiteImageFiles['hero_slide_3'] ? websiteImageFiles['hero_slide_3'].name : 'Upload Image'}
                                </Button>
                                {(websiteImageFiles['hero_slide_3'] || siteSettingsData?.website_images?.['hero_slide_3']) && (
                                    <img
                                        src={websiteImageFiles['hero_slide_3'] ? URL.createObjectURL(websiteImageFiles['hero_slide_3']) : (siteSettingsData.website_images['hero_slide_3']?.startsWith('http') ? siteSettingsData.website_images['hero_slide_3'] : api_urls + siteSettingsData.website_images['hero_slide_3'])}
                                        alt="Hero Slider – Slide 3"
                                        style={{ marginTop: '8px', width: '100%', height: '90px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #ddd', background: '#f5f5f5' }}
                                    />
                                )}
                            </div>
                        </Grid>
                        <Grid item lg={3} sm={12} md={6} xs={12}>
                            <div style={{ border: '1px solid #e0e0e0', borderRadius: '10px', padding: '14px', background: '#fafafa' }}>
                                <p style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px', color: '#333' }}>
                                    Hero Slider – Slide 4
                                </p>
                                <p style={{ fontSize: '11px', color: '#888', marginBottom: '8px' }}>Size: 520×520 px &nbsp;|&nbsp; Main hero carousel, Slide 4</p>
                                <input
                                    type="file"
                                    accept="image/*"
                                    ref={websiteImageRefs['hero_slide_4']}
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) setWebsiteImageFiles(prev => ({ ...prev, 'hero_slide_4': file }));
                                    }}
                                />
                                <Button variant="outlined" startIcon={<CloudUpload />} fullWidth
                                    onClick={() => websiteImageRefs['hero_slide_4']?.current?.click()}
                                    sx={{ height: '44px', textTransform: 'none', borderColor: '#ccc', color: '#555', fontSize: '12px' }}
                                >
                                    {websiteImageFiles['hero_slide_4'] ? websiteImageFiles['hero_slide_4'].name : 'Upload Image'}
                                </Button>
                                {(websiteImageFiles['hero_slide_4'] || siteSettingsData?.website_images?.['hero_slide_4']) && (
                                    <img
                                        src={websiteImageFiles['hero_slide_4'] ? URL.createObjectURL(websiteImageFiles['hero_slide_4']) : (siteSettingsData.website_images['hero_slide_4']?.startsWith('http') ? siteSettingsData.website_images['hero_slide_4'] : api_urls + siteSettingsData.website_images['hero_slide_4'])}
                                        alt="Hero Slider – Slide 4"
                                        style={{ marginTop: '8px', width: '100%', height: '90px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #ddd', background: '#f5f5f5' }}
                                    />
                                )}
                            </div>
                        </Grid>
                        <Grid item lg={3} sm={12} md={6} xs={12}>
                            <div style={{ border: '1px solid #e0e0e0', borderRadius: '10px', padding: '14px', background: '#fafafa' }}>
                                <p style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px', color: '#333' }}>
                                    About Iris Analysis Section
                                </p>
                                <p style={{ fontSize: '11px', color: '#888', marginBottom: '8px' }}>Size: 500×500 px &nbsp;|&nbsp; "What is Iris Analysis?" section image (with play button overlay)</p>
                                <input
                                    type="file"
                                    accept="image/*"
                                    ref={websiteImageRefs['about_section']}
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) setWebsiteImageFiles(prev => ({ ...prev, 'about_section': file }));
                                    }}
                                />
                                <Button variant="outlined" startIcon={<CloudUpload />} fullWidth
                                    onClick={() => websiteImageRefs['about_section']?.current?.click()}
                                    sx={{ height: '44px', textTransform: 'none', borderColor: '#ccc', color: '#555', fontSize: '12px' }}
                                >
                                    {websiteImageFiles['about_section'] ? websiteImageFiles['about_section'].name : 'Upload Image'}
                                </Button>
                                {(websiteImageFiles['about_section'] || siteSettingsData?.website_images?.['about_section']) && (
                                    <img
                                        src={websiteImageFiles['about_section'] ? URL.createObjectURL(websiteImageFiles['about_section']) : (siteSettingsData.website_images['about_section']?.startsWith('http') ? siteSettingsData.website_images['about_section'] : api_urls + siteSettingsData.website_images['about_section'])}
                                        alt="About Iris Analysis Section"
                                        style={{ marginTop: '8px', width: '100%', height: '90px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #ddd', background: '#f5f5f5' }}
                                    />
                                )}
                            </div>
                        </Grid>
                        <Grid item lg={3} sm={12} md={6} xs={12}>
                            <div style={{ border: '1px solid #e0e0e0', borderRadius: '10px', padding: '14px', background: '#fafafa' }}>
                                <p style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px', color: '#333' }}>
                                    Iris Analysis Benefits Section
                                </p>
                                <p style={{ fontSize: '11px', color: '#888', marginBottom: '8px' }}>Size: 420–500 px wide &nbsp;|&nbsp; "What does Iris Report Reveal?" section image</p>
                                <input
                                    type="file"
                                    accept="image/*"
                                    ref={websiteImageRefs['benefits_section']}
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) setWebsiteImageFiles(prev => ({ ...prev, 'benefits_section': file }));
                                    }}
                                />
                                <Button variant="outlined" startIcon={<CloudUpload />} fullWidth
                                    onClick={() => websiteImageRefs['benefits_section']?.current?.click()}
                                    sx={{ height: '44px', textTransform: 'none', borderColor: '#ccc', color: '#555', fontSize: '12px' }}
                                >
                                    {websiteImageFiles['benefits_section'] ? websiteImageFiles['benefits_section'].name : 'Upload Image'}
                                </Button>
                                {(websiteImageFiles['benefits_section'] || siteSettingsData?.website_images?.['benefits_section']) && (
                                    <img
                                        src={websiteImageFiles['benefits_section'] ? URL.createObjectURL(websiteImageFiles['benefits_section']) : (siteSettingsData.website_images['benefits_section']?.startsWith('http') ? siteSettingsData.website_images['benefits_section'] : api_urls + siteSettingsData.website_images['benefits_section'])}
                                        alt="Iris Analysis Benefits Section"
                                        style={{ marginTop: '8px', width: '100%', height: '90px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #ddd', background: '#f5f5f5' }}
                                    />
                                )}
                            </div>
                        </Grid>
                        <Grid item lg={3} sm={12} md={6} xs={12}>
                            <div style={{ border: '1px solid #e0e0e0', borderRadius: '10px', padding: '14px', background: '#fafafa' }}>
                                <p style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px', color: '#333' }}>
                                    Why Business is Powerful Section
                                </p>
                                <p style={{ fontSize: '11px', color: '#888', marginBottom: '8px' }}>Size: Max 500 px height &nbsp;|&nbsp; "What makes Iris Analysis Business Unique?" section image</p>
                                <input
                                    type="file"
                                    accept="image/*"
                                    ref={websiteImageRefs['why_powerful_section']}
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) setWebsiteImageFiles(prev => ({ ...prev, 'why_powerful_section': file }));
                                    }}
                                />
                                <Button variant="outlined" startIcon={<CloudUpload />} fullWidth
                                    onClick={() => websiteImageRefs['why_powerful_section']?.current?.click()}
                                    sx={{ height: '44px', textTransform: 'none', borderColor: '#ccc', color: '#555', fontSize: '12px' }}
                                >
                                    {websiteImageFiles['why_powerful_section'] ? websiteImageFiles['why_powerful_section'].name : 'Upload Image'}
                                </Button>
                                {(websiteImageFiles['why_powerful_section'] || siteSettingsData?.website_images?.['why_powerful_section']) && (
                                    <img
                                        src={websiteImageFiles['why_powerful_section'] ? URL.createObjectURL(websiteImageFiles['why_powerful_section']) : (siteSettingsData.website_images['why_powerful_section']?.startsWith('http') ? siteSettingsData.website_images['why_powerful_section'] : api_urls + siteSettingsData.website_images['why_powerful_section'])}
                                        alt="Why Business is Powerful Section"
                                        style={{ marginTop: '8px', width: '100%', height: '90px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #ddd', background: '#f5f5f5' }}
                                    />
                                )}
                            </div>
                        </Grid>
                        <Grid item lg={3} sm={12} md={6} xs={12}>
                            <div style={{ border: '1px solid #e0e0e0', borderRadius: '10px', padding: '14px', background: '#fafafa' }}>
                                <p style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px', color: '#333' }}>
                                    International Affiliation – USA Section
                                </p>
                                <p style={{ fontSize: '11px', color: '#888', marginBottom: '8px' }}>Size: Max 380 px height &nbsp;|&nbsp; "Global Presence / ARIDGE USA" section image</p>
                                <input
                                    type="file"
                                    accept="image/*"
                                    ref={websiteImageRefs['intl_affiliation_section']}
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) setWebsiteImageFiles(prev => ({ ...prev, 'intl_affiliation_section': file }));
                                    }}
                                />
                                <Button variant="outlined" startIcon={<CloudUpload />} fullWidth
                                    onClick={() => websiteImageRefs['intl_affiliation_section']?.current?.click()}
                                    sx={{ height: '44px', textTransform: 'none', borderColor: '#ccc', color: '#555', fontSize: '12px' }}
                                >
                                    {websiteImageFiles['intl_affiliation_section'] ? websiteImageFiles['intl_affiliation_section'].name : 'Upload Image'}
                                </Button>
                                {(websiteImageFiles['intl_affiliation_section'] || siteSettingsData?.website_images?.['intl_affiliation_section']) && (
                                    <img
                                        src={websiteImageFiles['intl_affiliation_section'] ? URL.createObjectURL(websiteImageFiles['intl_affiliation_section']) : (siteSettingsData.website_images['intl_affiliation_section']?.startsWith('http') ? siteSettingsData.website_images['intl_affiliation_section'] : api_urls + siteSettingsData.website_images['intl_affiliation_section'])}
                                        alt="International Affiliation – USA Section"
                                        style={{ marginTop: '8px', width: '100%', height: '90px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #ddd', background: '#f5f5f5' }}
                                    />
                                )}
                            </div>
                        </Grid>
                </Grid>

                {/* About Page Images */}
                <Grid container spacing={2} sx={{ marginTop: '20px' }}>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                        <DatatableHeading title="About Page Images" sub_title="Upload images for the About Us page sections." />
                    </Grid>
                    <Grid item lg={3} sm={12} md={6} xs={12}>
                        <div style={{ border: '1px solid #e0e0e0', borderRadius: '10px', padding: '14px', background: '#fafafa' }}>
                            <p style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px', color: '#333' }}>
                                AI Technology Section
                            </p>
                            <p style={{ fontSize: '11px', color: '#888', marginBottom: '8px' }}>Size: 500×500 px &nbsp;|&nbsp; "AI Technology" section image (about page bottom)</p>
                            <input
                                type="file"
                                accept="image/*"
                                ref={websiteImageRefs['about_ai_section']}
                                style={{ display: 'none' }}
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) setWebsiteImageFiles(prev => ({ ...prev, 'about_ai_section': file }));
                                }}
                            />
                            <Button variant="outlined" startIcon={<CloudUpload />} fullWidth
                                onClick={() => websiteImageRefs['about_ai_section']?.current?.click()}
                                sx={{ height: '44px', textTransform: 'none', borderColor: '#ccc', color: '#555', fontSize: '12px' }}
                            >
                                {websiteImageFiles['about_ai_section'] ? websiteImageFiles['about_ai_section'].name : 'Upload Image'}
                            </Button>
                            {(websiteImageFiles['about_ai_section'] || siteSettingsData?.website_images?.['about_ai_section']) && (
                                <img
                                    src={websiteImageFiles['about_ai_section'] ? URL.createObjectURL(websiteImageFiles['about_ai_section']) : (siteSettingsData.website_images['about_ai_section']?.startsWith('http') ? siteSettingsData.website_images['about_ai_section'] : api_urls + siteSettingsData.website_images['about_ai_section'])}
                                    alt="AI Technology Section"
                                    style={{ marginTop: '8px', width: '100%', height: '90px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #ddd', background: '#f5f5f5' }}
                                />
                            )}
                        </div>
                    </Grid>
                </Grid>

                {/* Remedies Page Images */}
                <Grid container spacing={2} sx={{ marginTop: '20px' }}>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                        <DatatableHeading title="Remedies Page Images" sub_title="Upload images for the 4 sections of the Remedies page (same fallback image used if not set)." />
                    </Grid>
                    {['remedies_1','remedies_2','remedies_3','remedies_4'].map((key, idx) => (
                        <Grid item lg={3} md={4} sm={6} xs={12} key={key}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <label style={{ fontSize: '12px', color: '#666', fontWeight: 500 }}>Section {idx + 1} Image</label>
                                <input type="file" accept="image/*" ref={websiteImageRefs[key]} style={{ display: 'none' }} onChange={e => { if(e.target.files[0]) setWebsiteImageFiles(p => ({...p, [key]: e.target.files[0]})); }} />
                                <Button variant="outlined" onClick={() => websiteImageRefs[key]?.current?.click()} sx={{ height: '44px', textTransform: 'none', borderColor: '#ccc', color: '#555', fontSize: '12px' }}>
                                    {websiteImageFiles[key] ? websiteImageFiles[key].name : 'Upload Image'}
                                </Button>
                                {(websiteImageFiles[key] || siteSettingsData?.website_images?.[key]) && (
                                    <img src={websiteImageFiles[key] ? URL.createObjectURL(websiteImageFiles[key]) : (siteSettingsData.website_images[key]?.startsWith('http') ? siteSettingsData.website_images[key] : api_urls + siteSettingsData.website_images[key])} alt={`Remedies Section ${idx+1}`} style={{ marginTop: '8px', width: '100%', height: '90px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #ddd', background: '#f5f5f5' }} />
                                )}
                            </div>
                        </Grid>
                    ))}
                </Grid>

                {/* USA Affiliation Page Images */}
                <Grid container spacing={2} sx={{ marginTop: '20px' }}>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                        <DatatableHeading title="USA Affiliation Page Images" sub_title="Upload images for the 4 sections of the USA Affiliation page." />
                    </Grid>
                    {['usa_affiliation_1','usa_affiliation_2','usa_affiliation_3','usa_affiliation_4'].map((key, idx) => (
                        <Grid item lg={3} md={4} sm={6} xs={12} key={key}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <label style={{ fontSize: '12px', color: '#666', fontWeight: 500 }}>Section {idx + 1} Image</label>
                                <input type="file" accept="image/*" ref={websiteImageRefs[key]} style={{ display: 'none' }} onChange={e => { if(e.target.files[0]) setWebsiteImageFiles(p => ({...p, [key]: e.target.files[0]})); }} />
                                <Button variant="outlined" onClick={() => websiteImageRefs[key]?.current?.click()} sx={{ height: '44px', textTransform: 'none', borderColor: '#ccc', color: '#555', fontSize: '12px' }}>
                                    {websiteImageFiles[key] ? websiteImageFiles[key].name : 'Upload Image'}
                                </Button>
                                {(websiteImageFiles[key] || siteSettingsData?.website_images?.[key]) && (
                                    <img src={websiteImageFiles[key] ? URL.createObjectURL(websiteImageFiles[key]) : (siteSettingsData.website_images[key]?.startsWith('http') ? siteSettingsData.website_images[key] : api_urls + siteSettingsData.website_images[key])} alt={`USA Affiliation Section ${idx+1}`} style={{ marginTop: '8px', width: '100%', height: '90px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #ddd', background: '#f5f5f5' }} />
                                )}
                            </div>
                        </Grid>
                    ))}
                </Grid>

                {/* Business Opportunity Page Images */}
                <Grid container spacing={2} sx={{ marginTop: '20px' }}>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                        <DatatableHeading title="Business Opportunity Page Images" sub_title="Upload images for the 4 sections of the Business Opportunity page." />
                    </Grid>
                    {['biz_opportunity_1','biz_opportunity_2','biz_opportunity_3','biz_opportunity_4'].map((key, idx) => (
                        <Grid item lg={3} md={4} sm={6} xs={12} key={key}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <label style={{ fontSize: '12px', color: '#666', fontWeight: 500 }}>Section {idx + 1} Image</label>
                                <input type="file" accept="image/*" ref={websiteImageRefs[key]} style={{ display: 'none' }} onChange={e => { if(e.target.files[0]) setWebsiteImageFiles(p => ({...p, [key]: e.target.files[0]})); }} />
                                <Button variant="outlined" onClick={() => websiteImageRefs[key]?.current?.click()} sx={{ height: '44px', textTransform: 'none', borderColor: '#ccc', color: '#555', fontSize: '12px' }}>
                                    {websiteImageFiles[key] ? websiteImageFiles[key].name : 'Upload Image'}
                                </Button>
                                {(websiteImageFiles[key] || siteSettingsData?.website_images?.[key]) && (
                                    <img src={websiteImageFiles[key] ? URL.createObjectURL(websiteImageFiles[key]) : (siteSettingsData.website_images[key]?.startsWith('http') ? siteSettingsData.website_images[key] : api_urls + siteSettingsData.website_images[key])} alt={`Business Opportunity Section ${idx+1}`} style={{ marginTop: '8px', width: '100%', height: '90px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #ddd', background: '#f5f5f5' }} />
                                )}
                            </div>
                        </Grid>
                    ))}
                </Grid>

                {/* Partner Landing Page Images */}
                <Grid container spacing={2} sx={{ marginTop: '20px' }}>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                        <DatatableHeading title="Partner Landing Page Images" sub_title="Upload images for the Partner landing page sections." />
                    </Grid>
                    {[
                        { key: 'partner_benefit_section', label: '"How will this Assessment Benefit you?" Section' },
                        { key: 'partner_step_1', label: '3 Steps – Step 1 (Apply for Franchise)' },
                        { key: 'partner_step_2', label: '3 Steps – Step 2 (Training & Exam)' },
                        { key: 'partner_step_3', label: '3 Steps – Step 3 (Launch Business)' },
                    ].map(({ key, label }) => (
                        <Grid item lg={3} md={4} sm={6} xs={12} key={key}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <label style={{ fontSize: '12px', color: '#666', fontWeight: 500 }}>{label}</label>
                                <input type="file" accept="image/*" ref={websiteImageRefs[key]} style={{ display: 'none' }} onChange={e => { if(e.target.files[0]) setWebsiteImageFiles(p => ({...p, [key]: e.target.files[0]})); }} />
                                <Button variant="outlined" onClick={() => websiteImageRefs[key]?.current?.click()} sx={{ height: '44px', textTransform: 'none', borderColor: '#ccc', color: '#555', fontSize: '12px' }}>
                                    {websiteImageFiles[key] ? websiteImageFiles[key].name : 'Upload Image'}
                                </Button>
                                {(websiteImageFiles[key] || siteSettingsData?.website_images?.[key]) && (
                                    <img src={websiteImageFiles[key] ? URL.createObjectURL(websiteImageFiles[key]) : (siteSettingsData.website_images[key]?.startsWith('http') ? siteSettingsData.website_images[key] : api_urls + siteSettingsData.website_images[key])} alt={label} style={{ marginTop: '8px', width: '100%', height: '90px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #ddd', background: '#f5f5f5' }} />
                                )}
                            </div>
                        </Grid>
                    ))}
                </Grid>

                                {/* Submit Button */}
                <Grid container spacing={2} justifyContent="flex-end" sx={{ marginTop: '20px' }}>
                    <Grid item>
                        <div onClick={handleSubmit} style={{ padding: '10px 30px', border: `1px solid ${Color.primary}`, borderRadius: '5px', background: "linear-gradient(to right,  #6653E8, #9544EB)", color: Color.white, fontSize: '14px', cursor: 'pointer', fontWeight: '500' }}>
                            Save Settings
                        </div>
                    </Grid>
                </Grid>
            </div>
        </>
    );
};

export default SiteSettings;
